import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { fmtRoDate } from "../lib/dateFmt";
import { History, ArrowLeft } from "lucide-react";

export default function IstoricDemontari() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    api.get("/projects/dismantle-history?limit=500")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.adresa || "").toLowerCase().includes(q) ||
        (r.dismantled_by_user || "").toLowerCase().includes(q) ||
        (r.persons || []).some((p) => (p.name || "").toLowerCase().includes(q))
      );
    }
    if (from) out = out.filter((r) => (r.dismantle_date || "") >= from);
    if (to) out = out.filter((r) => (r.dismantle_date || "") <= to);
    return out;
  }, [rows, search, from, to]);

  const totalProjects = filtered.length;
  const totalPersons = useMemo(() => {
    const set = new Set();
    filtered.forEach((r) => (r.persons || []).forEach((p) => set.add(p.id)));
    return set.size;
  }, [filtered]);

  return (
    <div data-testid="page-istoric-demontari">
      <div className="flex items-center gap-3 mb-6">
        <Button asChild variant="outline" size="sm">
          <Link to="/proiecte"><ArrowLeft size={16} className="mr-1.5"/>Înapoi</Link>
        </Button>
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
            <History size={28} className="text-emerald-600"/>
            Istoric demontări
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Toate proiectele demontate, cu echipa care a executat demontajul și data exactă.</p>
        </div>
      </div>

      {/* Filtre */}
      <div className="card-flat p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-xs text-zinc-500">Caută (proiect / adresă / persoană)</Label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ex: Chur, Khreis..." className="mt-1" data-testid="dismantle-search"/>
        </div>
        <div>
          <Label className="text-xs text-zinc-500">De la</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1" data-testid="dismantle-from"/>
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Până la</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1" data-testid="dismantle-to"/>
        </div>
      </div>

      {/* Sumar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="card-flat p-3 text-center">
          <div className="text-3xl font-display font-extrabold text-emerald-600">{totalProjects}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Proiecte demontate</div>
        </div>
        <div className="card-flat p-3 text-center">
          <div className="text-3xl font-display font-extrabold text-blue-600">{totalPersons}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Persoane diferite implicate</div>
        </div>
        <div className="card-flat p-3 text-center hidden sm:block">
          <div className="text-3xl font-display font-extrabold text-amber-600">
            {filtered.length > 0 ? Math.round(filtered.reduce((s, r) => s + (r.days_active || 0), 0) / filtered.length) : 0}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Durată medie zile</div>
        </div>
      </div>

      {/* Listă */}
      {loading ? (
        <div className="text-center text-zinc-400 p-12">Se încarcă...</div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-12 text-center text-zinc-400 italic">Niciun proiect demontat încă.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <Link
              key={r.project_id}
              to={`/proiecte?project=${r.project_id}&edit=1`}
              className="card-flat p-4 block hover:border-emerald-300 transition-colors border-l-4"
              style={{ borderLeftColor: r.color || "#16A34A" }}
              data-testid={`dismantle-row-${r.project_id}`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded">✅ DEMONTAT</span>
                    <h3 className="font-display font-bold text-lg truncate">{r.name}</h3>
                    {r.days_active != null && (
                      <span className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">
                        {r.days_active} {r.days_active === 1 ? "zi" : "zile"} activ
                      </span>
                    )}
                  </div>
                  {r.adresa && <div className="text-sm text-zinc-500 mt-0.5">📍 {r.adresa}</div>}
                  <div className="text-xs text-zinc-600 mt-1.5 space-y-0.5">
                    <div>
                      📅 <strong>Demontat pe {fmtRoDate(r.dismantle_date)}</strong>
                      {r.dismantled_by_user && (
                        <span className="text-zinc-400"> · marcat în sistem de <strong className="text-zinc-500">{r.dismantled_by_user}</strong></span>
                      )}
                    </div>
                    {r.start_date && r.end_date && (
                      <div className="text-zinc-400">🗓 Perioadă lucrare: {fmtRoDate(r.start_date)} – {fmtRoDate(r.end_date)}</div>
                    )}
                    {r.actual_total_hours > 0 && (
                      <div className="text-orange-700 font-semibold">⏱ <strong>{r.actual_total_hours}h</strong> ore reale logate de echipă{r.actual_dates && r.actual_dates.length > 0 && ` (${r.actual_dates.map(fmtRoDate).join(", ")})`}</div>
                    )}
                  </div>
                  {/* Echipa REALĂ — pe baza orelor logate (kind=dismantle) */}
                  {r.persons_actual && r.persons_actual.length > 0 && (
                    <div className="mt-2 flex items-start gap-1.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider text-orange-700 font-bold mt-0.5">⏱ Echipa reală (ore logate):</span>
                      {r.persons_actual.map((p) => (
                        <span
                          key={`act-${p.id}`}
                          className="text-[11px] px-2 py-0.5 rounded font-semibold inline-flex items-center gap-1"
                          style={{ background: p.color || "#FFEDD5", color: p.color ? "white" : "#9A3412" }}
                          title={`${p.hours}h în ${(p.dates || []).join(", ")}`}
                        >
                          {p.name}
                          <span className="text-[9px] font-bold bg-white/40 px-1 rounded">{p.hours}h</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Echipa PLANIFICATĂ — la marcarea „Demontat" în Planificator */}
                  {r.persons_planned && r.persons_planned.length > 0 && (
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">📌 Echipa planificată:</span>
                      {r.persons_planned.map((p) => (
                        <span key={`pln-${p.id}`} className="text-[11px] px-2 py-0.5 rounded font-semibold" style={{ background: p.color || "#E5E7EB", color: p.color ? "white" : "#71717A" }}>
                          {p.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Mașini reale + planificate */}
                  {r.cars && r.cars.length > 0 && (
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">🚗 Mașini:</span>
                      {r.cars.map((c) => (
                        <span key={c.id} className="text-[11px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-semibold">
                          {c.label}
                        </span>
                      ))}
                    </div>
                  )}
                  {r.dismantled_note && (
                    <div className="mt-1 text-xs text-zinc-500 italic bg-zinc-50 px-2 py-1 rounded">📝 {r.dismantled_note}</div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
